import { Plus } from "lucide-react";
import * as React from "react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
  value: string;
  count: number;
}

/** Always the last row once there's typed text — lets you commit exactly
 * what you typed even when it partially matches an existing option, not
 * just when nothing matches. Deliberately NOT a `CommandItem`: cmdk hides
 * any item with a zero match score whenever there's a search query, and
 * gives it a real score only via fuzzy-matching its `value` against that
 * query — there's no way to get both "always visible" and "always last"
 * from cmdk's own scoring/reorder system. A plain, unregistered row sits
 * outside that system entirely, so it's untouched by cmdk's filtering and
 * DOM-reordering and just stays exactly where it's rendered. */
function CreateOption({ search, onCreate }: { search: string; onCreate: () => void }) {
  if (!search.trim()) return null;
  return (
    <div
      onMouseDown={(e) => e.preventDefault()}
      onClick={onCreate}
      className="relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none hover:bg-muted"
    >
      <Plus className="size-4" />
      Add &quot;{search.trim()}&quot;
    </div>
  );
}

/** A free-text field that suggests from a fixed list of existing values —
 * not a strict picker; whatever's typed is committed when the popover
 * closes, matching a suggestion is just a shortcut. `options` is expected
 * pre-sorted (most-used first); cmdk preserves that order until there's a
 * query to filter by. */
function Combobox({
  value,
  onChange,
  options,
  id,
  placeholder,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  id?: string;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState(value);

  const commitAndClose = (finalValue: string) => {
    onChange(finalValue.trim());
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setSearch(value);
          setOpen(true);
        } else {
          commitAndClose(search);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          className={cn(
            "h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-left text-base outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 md:text-sm",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="block truncate">{value || placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
        <Command>
          <CommandInput value={search} onValueChange={setSearch} placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No matches</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => commitAndClose(option.value)}
                  className={cn(option.value === value && "font-medium")}
                >
                  <span className="truncate">{option.value}</span>
                  <CommandShortcut className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-normal tracking-normal text-muted-foreground">
                    {option.count}
                  </CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
            <CreateOption search={search} onCreate={() => commitAndClose(search)} />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { Combobox };
